import { serviceTemplateToShopServiceTree } from 'helpers/bookingTemplates';
import { IOffice } from 'models/shop/office';
import { IServiceGroup } from 'models/shop/serviceGroups';
import {
	ISaveShopServiceTemplate,
	IShopServiceTemplate,
	IShopServiceTemplateItem,
	IShopServiceTemplates,
	IShopServiceTemplatesAction,
	IShopServiceTreeItem,
} from 'models/shop/serviceTemplates';
import { IServicesList } from 'models/shop/shopBookingTemplates';
import { IShopCard } from 'models/shop/shopcard';
import { requestHelper } from 'redux/helpers';
import { IStore } from 'redux/IStore';
import { v5 as uuidv5 } from 'uuid';
import { getGuidFromString } from '../../../../helpers/common';
import { deleteBookingTemplate, IApiService, saveGroupedBookingTemplate } from '../bookingtemplates/helpers';
import { fetchBookingTemplateVariants, getOfficeBookingTemplates, sequential } from '../helpers';
import { addServiceGroup, getServiceGroupsByOffice, saveOfficeServiceGroups } from '../serviceGroups/helpers';

/** Action Types */
export const GET_REQUEST: string = 'shopServiceTemplates/GET_REQUEST';
export const GET_SUCCESS: string = 'shopServiceTemplates/GET_SUCCESS';

export const MANAGE_REQUEST: string = 'shopServiceTemplates/MANAGE_REQUEST';
export const MANAGE_SUCCESS: string = 'shopServiceTemplates/MANAGE_SUCCESS';

export const REQUEST_FAILURE: string = 'shopServiceTemplates/REQUEST_FAILURE';

export const RESET_DATA: string = 'shopServiceTemplates/RESET_DATA';

/** Initial State */
const initialState: IShopServiceTemplates = {
	shopServiceTemplates: [],
};

/** Reducer */
export function shopServiceTemplatesReducer(
	state = initialState,
	action: IShopServiceTemplatesAction,
): IShopServiceTemplates {
	switch (action.type) {
		case MANAGE_REQUEST:
		case GET_REQUEST:
			return { ...state, isFetching: true, error: false };

		case MANAGE_SUCCESS:
		case GET_SUCCESS:
			return { ...state, isFetching: false, shopServiceTemplates: action.payload?.shopServiceTemplates || [] };

		case REQUEST_FAILURE:
			return { ...state, isFetching: false, message: action.payload.message, error: true };

		case RESET_DATA:
			return { ...initialState };

		default:
			return state;
	}
}

/** Async Action Creator */
export function getServiceTemplatesByShop(shopId: string) {
	return (dispatch, getState: () => IStore): Promise<boolean> => {
		if (shopId) {
			dispatch(getShopServiceTemplatesRequest());
			return requestHelper<IShopServiceTemplate[]>(
				fetch(
					getState().fetchDefault.baseAPI + 'api/Shop/' + shopId + '/GetTemplatesByShop',
					getState().fetchDefault.requestInit,
				),
			)
				.then((templates) => {
					dispatch(getShopServiceTemplatesSuccess(templates));
					return true;
				})
				.catch((err) => {
					dispatch(requestFailure(err));
					return false;
				});
		} else {
			dispatch(resetServiceTemplatesData());
			return Promise.resolve(false);
		}
	};
}

export function getServiceTemplateDetails(templateName: string, force = false) {
	return (dispatch, getState: () => IStore): Promise<boolean> => {
		if (!templateName) {
			console.error('templateName missing');
			return Promise.resolve(false);
		}
		const existingTemplates = getState().shopServiceTemplates?.shopServiceTemplates || [];
		const existingIndex = existingTemplates.findIndex((st) => st.templateName === templateName);
		if (existingIndex < 0) {
			console.error('template does not exist');
			return Promise.resolve(false);
		}
		const existing = existingTemplates[existingIndex];
		if (existing.detailed && !force) {
			return Promise.resolve(true);
		}
		dispatch(getShopServiceTemplatesRequest());
		return requestHelper<IShopServiceTemplate>(
			fetch(
				getState().fetchDefault.baseAPI + 'api/Shop/Templates/' + existing.officeId + '/' + templateName,
				getState().fetchDefault.requestInit,
			),
		)
			.then((template) => {
				const copy = [...existingTemplates];
				copy[existingIndex] = {
					...template,
					detailed: true,
				};
				dispatch(getShopServiceTemplatesSuccess(copy));
				return true;
			})
			.catch((err) => {
				dispatch(requestFailure(err));
				return false;
			});
	};
}

// Used for both saving and updating templates
export function saveShopServiceTemplate(mode: 'new' | 'existing', template: ISaveShopServiceTemplate) {
	return (dispatch, getState: () => IStore): Promise<boolean> => {
		dispatch(manageShopServiceTemplatesRequest());
		return requestHelper<IShopServiceTemplate[]>(
			fetch(getState().fetchDefault.baseAPI + 'api/Shop/Templates', {
				...getState().fetchDefault.requestInit,
				method: mode === 'new' ? 'POST' : 'PUT',
				body: JSON.stringify(template),
			}),
		)
			.then(() => {
				dispatch(manageShopServiceTemplatesSuccess());
				return true;
			})
			.catch((err) => {
				dispatch(requestFailure(err));
				return false;
			});
	};
}

export function applyShopServiceTemplate(templateName: string, officeId: IOffice['id'], chosenTemplates: string[]) {
	return async (dispatch, getState: () => IStore): Promise<boolean> => {
		if (!templateName || !officeId) {
			console.error('templateName or officeId missing');
			return Promise.resolve(false);
		}

		const state = getState();
		const shopCard: Partial<IShopCard> = state.shopcard.shopcard || {};
		const shopId = shopCard.id;
		const template = state.shopServiceTemplates.shopServiceTemplates.find(
			(st) => st.templateName === templateName && st.detailed && st.templateItems,
		);

		if (!template) {
			console.error('Detailed template missing');
			return Promise.resolve(false);
		}

		const validOfficeIds = shopCard.offices?.map((o) => o.id) || [];

		if (!validOfficeIds.includes(template.officeId)) {
			console.error('Invalid template.officeId');
			return Promise.resolve(false);
		}

		if (!validOfficeIds.includes(officeId)) {
			console.error('Invalid officeId');
			return Promise.resolve(false);
		}

		dispatch(manageShopServiceTemplatesRequest());

		const _officeBookingTemplatesRequest = getOfficeBookingTemplates(getState, officeId, false);
		const officeServices: IServicesList[] = await _officeBookingTemplatesRequest;

		// Delete bundles first and let service group deletion cascade delete the rest (constraint if deleting services inside a bundle)
		const bundlesToDelete = officeServices.filter((ser) => ser?.customData && JSON.parse(ser?.customData)?.isBundle);

		await sequential(bundlesToDelete.map((s) => () => deleteBookingTemplate(getState, s.id)));

		// Filter to take only chosen items from the whole template
		const tempTemplateItems = (template?.templateItems?.length && [...template.templateItems]) || [];
		const filteredTemplate: IShopServiceTemplate = {
			...template,
			templateItems: tempTemplateItems.filter((item) => chosenTemplates.indexOf(item.id) !== -1),
		};
		const templateTree = serviceTemplateToShopServiceTree(filteredTemplate);

		const deleteServiceGroupsForOffice = (
			getState: () => IStore,
			officeId: string,
			groups: IServiceGroup[],
		): Promise<any> => {
			const serviceGroupIdsToDelete = groups.filter((sg1) => !sg1.parentId).map((sg2) => sg2.id); // Delete only parent service groups as BE cascade deletes the child groups and booking templates

			return saveOfficeServiceGroups(getState, officeId, [], [], serviceGroupIdsToDelete).catch((e) =>
				console.error(e),
			);
		};

		// Also saves new ids
		const createServiceGroupsForOffice = (items: IShopServiceTreeItem[], officeId: string): Promise<any> => {
			const saveRecursive = (parentItem: IShopServiceTreeItem) => (item: IShopServiceTreeItem) => (): Promise<any> => {
				let promise: Promise<any> = Promise.resolve(1);

				if (item.type === 'serviceGroup') {
					promise = addServiceGroup(getState, {
						...item,
						officeId,
						shopId,
						value: item.name,
						id: undefined,
						order: item.order,
						type: null,
						parentId: parentItem?.type === 'serviceCategory' ? undefined : parentItem?.id,
					}).then((sg) => {
						item.id = sg.id;
						if (parentItem) {
							item.parentServiceGroupId = parentItem.id;
						}
					});
				} else if (item.type === 'service') {
					const titem: IShopServiceTemplateItem = {
						...(item as any),
						name: item.name,
						officeId,
						shopId,
						id: undefined,
						serviceGroupId: parentItem.id,
						parentServiceGroupId: parentItem.parentServiceGroupId,
					};
					promise = saveServiceFromTemplateItem(titem, officeId);
				}

				if (item.type === 'service') {
					// child booking templates are already saved
					return promise;
				}

				return promise.then(() => sequential((item.childrenTemplateItems || []).map(saveRecursive(item))));
			};

			return sequential(items.map(saveRecursive(null)));
		};

		const saveServiceFromTemplateItem = (item: IShopServiceTemplateItem, officeId: IOffice['id']): Promise<any> => {
			const subItems = item.childrenTemplateItems || [];
			const variantParentId = uuidv5(String(Math.random()), shopId);
			const servicesWithSelf: IApiService[] = [item, ...subItems].map((si) => ({
				...(si as any),
				name: si.name,
				shopId,
				officeId,
				duration: si.duration,
				price: si.price || si.minimumPrice,
				categoryKey: item.categoryKey,
				resources: [],
				parentServiceGroupId: item.parentServiceGroupId,
				serviceGroupId: item.serviceGroupId,
				// If 'si' is a subitem, assign new variant parent id
				variantParentId: item == si ? si.variantParentId : variantParentId,
				sku: [variantParentId, officeId, si.price || si.minimumPrice, item.duration].join('-'),
				id: undefined,
				childrenTemplateItems: [],
			}));

			return saveGroupedBookingTemplate(getState, servicesWithSelf.slice(1), shopId)
				.then((res: string) => {
					// returns location with variantParentId guid
					const returnedVariantParentId = getGuidFromString(res);
					if (!returnedVariantParentId) {
						throw new Error('No variantparentid returned');
					}
					return fetchBookingTemplateVariants(getState, returnedVariantParentId);
				})
				.then((services) => {
					if (!services?.length) {
						throw new Error('Service not saved for variantParentId ' + variantParentId);
					}
					return services.map((s) => s.id);
				});
		};

		return getServiceGroupsByOffice(getState, officeId)
			.then((serviceGroups) =>
				sequential([
					() => deleteServiceGroupsForOffice(getState, officeId, serviceGroups),
					() => createServiceGroupsForOffice(templateTree, officeId),
				]),
			)

			.then(() => {
				dispatch(manageShopServiceTemplatesSuccess());
				return true;
			})
			.catch((err) => {
				dispatch(requestFailure(err));
				return false;
			});
	};
}

export function deleteShopServiceTemplate(templateName: string, officeId: string) {
	return (dispatch, getState: () => IStore): Promise<boolean> => {
		dispatch(manageShopServiceTemplatesRequest());
		return requestHelper<IShopServiceTemplate[]>(
			fetch(
				getState().fetchDefault.baseAPI + 'api/Shop/Templates' + `/${officeId}/${templateName}`,
				Object.assign({}, getState().fetchDefault.requestInit, {
					method: 'DELETE',
				}),
			),
		)
			.then(() => {
				dispatch(manageShopServiceTemplatesSuccess());
				return true;
			})
			.catch((err) => {
				dispatch(requestFailure(err));
				return false;
			});
	};
}

// /** Action Creator */
export function manageShopServiceTemplatesRequest(): IShopServiceTemplatesAction {
	return {
		type: MANAGE_REQUEST,
	};
}
export function getShopServiceTemplatesRequest(): IShopServiceTemplatesAction {
	return {
		type: GET_REQUEST,
		payload: {
			shopServiceTemplates: [],
		},
	};
}

// /** Action Creator */
export function manageShopServiceTemplatesSuccess(): IShopServiceTemplatesAction {
	return {
		type: MANAGE_SUCCESS,
	};
}
export function getShopServiceTemplatesSuccess(
	shopServiceTemplates: IShopServiceTemplate[],
): IShopServiceTemplatesAction {
	return {
		type: GET_SUCCESS,
		payload: {
			shopServiceTemplates,
		},
	};
}

export function requestFailure(message: any): IShopServiceTemplatesAction {
	return {
		type: REQUEST_FAILURE,
		payload: {
			message,
		},
	};
}

// /** Action Creator */
export function resetServiceTemplatesData(): IShopServiceTemplatesAction {
	return {
		type: RESET_DATA,
	};
}
